/**
 * DesignSystem — living reference page for the Cadenza component library.
 *
 * Render every component in every meaningful state. When you ship a new
 * primitive, ADD IT HERE. This page is how design + engineering keep parity
 * and how new contributors discover what already exists.
 */
import { useState } from "react";
import { Server, Layers, AlertCircle, Info, Copy, Check } from "lucide-react";
import type { Edge, Node } from "@xyflow/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSub,
  CardContent,
} from "@/components/ui/card";
import { KpiCard } from "@/components/ui/kpi-card";
import { Avatar } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Stepper } from "@/components/ui/stepper";
import { RollingNumber } from "@/components/ui/rolling-number";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import {
  Skeleton,
  TableSkeleton,
  CardGridSkeleton,
  FormSkeleton,
  PanelSkeleton,
} from "@/components/ui/skeleton";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";
import { SitePickerInline } from "@/components/app/dashboard/site-picker-inline";
import { RangePicker } from "@/components/app/dashboard/range-picker";
import { DeviceClassCard } from "@/components/app/dashboard/device-class-card";
import { StatTile } from "@/components/app/dashboard/stat-tile";
import { ThroughputChartSurface } from "@/components/app/dashboard/throughput-chart-surface";
import { ChartStat } from "@/components/app/dashboard/chart-stat";
import { ApplianceStatusRail } from "@/components/app/dashboard/appliance-status-rail";
import { DashboardAiSummaryPanel } from "@/components/app/dashboard/ai-summary-panel";
import { FirmwareUpgradesSection } from "@/components/app/dashboard/firmware-upgrades-section";
import { AlertsSection } from "@/components/app/dashboard/alerts-section";
import { ConnectedClientsTable } from "@/components/app/dashboard/connected-clients-table";
import { DevicesTable } from "@/components/app/dashboard/devices-table";
import {
  TopologyExplorer,
  type TopologyExplorerNodeDetails,
} from "@/components/app/topology/topology-explorer";
import {
  OpusHandsontable,
  type OpusColumnConfig,
} from "@/components/ui/opus-handsontable";
import { useTheme } from "@/stores/theme";
import type {
  AlertHistoryRow,
  AssuranceAlert,
  ConnectedClient,
  FirmwareUpgrade,
  SiteDevice,
  WirelessFailedConnection,
} from "@/api/schemas/sites";
import HotTable from "@handsontable/react-wrapper";

/* Small helpers used only on this page. */
function Section({
  title,
  children,
  hint,
  code,
  className,
}: {
  title: string;
  children: React.ReactNode;
  hint?: string;
  code?: string;
  className?: string;
}) {
  const animatedSectionRef = useUiAutoAnimate();
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <section
      className={`flex flex-col gap-3 border-t border-line pt-8 ${className ?? ""}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="m-0 text-lg font-bold tracking-tight text-fg">
            {title}
          </h2>
          {hint ? (
            <p className="mt-1 border-l-2 border-aurora/40 pl-3 text-xs text-fg-mute">
              {hint}
            </p>
          ) : null}
        </div>
        {code ? (
          <button
            type="button"
            onClick={handleCopy}
            className="flex shrink-0 items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1 text-xs font-medium text-fg-mute transition-colors hover:border-line-strong hover:text-fg"
          >
            {copied ? (
              <Check size={12} className="text-mint" />
            ) : (
              <Copy size={12} />
            )}
            {copied ? "Copied!" : "Copy"}
          </button>
        ) : null}
      </div>
      <Card ref={animatedSectionRef}>
        <CardContent className="flex flex-wrap items-center gap-3">
          {children}
        </CardContent>
      </Card>
    </section>
  );
}

function DemoVariantCard({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-line/70 bg-surface-tint/20 p-3">
      <div className="mb-2 text-xs font-semibold text-fg">{label}</div>
      {children}
    </div>
  );
}

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-fg-mute">
      {children}
    </div>
  );
}

function Swatch({
  name,
  varName,
  hex,
}: {
  name: string;
  varName: string;
  hex?: string;
}) {
  return (
    <div className="flex w-[112px] flex-col gap-1.5">
      <div
        className="h-14 rounded-lg border border-line"
        style={{ background: `rgb(var(${varName}))` }}
      />
      <div className="font-mono text-[10px] text-fg">{name}</div>
      <div className="font-mono text-[9.5px] text-fg-mute">
        var({varName}){hex ? ` · ${hex}` : null}
      </div>
    </div>
  );
}

const SAMPLE_COLS = "1.2fr 1fr 0.7fr 0.5fr";

const DS_RANGE_OPTIONS = [
  { id: "1h", label: "Last 1 hour" },
  { id: "24h", label: "Last 24 hours" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
];
const DS_CUSTOM_RANGE_PREFIX = "custom:";

function parseCustomRange(
  rangeId: string,
): { from: number; to: number } | null {
  if (!rangeId.startsWith(DS_CUSTOM_RANGE_PREFIX)) return null;
  const [fromDate, toDate] = rangeId
    .slice(DS_CUSTOM_RANGE_PREFIX.length)
    .split("|");
  if (!fromDate || !toDate) return null;
  const from = new Date(`${fromDate}T00:00:00`).getTime();
  const to = new Date(`${toDate}T23:59:59.999`).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || from > to) return null;
  return { from, to };
}

function rangeLabel(rangeId: string): string {
  const preset = DS_RANGE_OPTIONS.find((r) => r.id === rangeId);
  if (preset) return preset.label;
  const custom = parseCustomRange(rangeId);
  if (!custom) return "Last 24 hours";
  const from = new Date(custom.from).toLocaleDateString();
  const to = new Date(custom.to).toLocaleDateString();
  return `Custom: ${from} - ${to}`;
}

function fmtBps(bps: number): string {
  if (bps < 1_000) return `${bps.toFixed(0)} b/s`;
  if (bps < 1_000_000) return `${(bps / 1_000).toFixed(0)} Kb/s`;
  if (bps < 1_000_000_000) return `${(bps / 1_000_000).toFixed(1)} Mb/s`;
  return `${(bps / 1_000_000_000).toFixed(1)} Gb/s`;
}

function fmtBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes.toFixed(0)} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

const _dsKeyFmtCache = new Map<string, Intl.DateTimeFormat>();
const _dsLabelFmtCache = new Map<string, Intl.DateTimeFormat>();

function dayKeyForTimezone(iso: string, timezone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "invalid";
  let fmt = _dsKeyFmtCache.get(timezone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" });
    _dsKeyFmtCache.set(timezone, fmt);
  }
  const parts = fmt.formatToParts(d);
  const year = parts.find((p) => p.type === "year")?.value ?? "0000";
  const month = parts.find((p) => p.type === "month")?.value ?? "00";
  const day = parts.find((p) => p.type === "day")?.value ?? "00";
  return `${year}-${month}-${day}`;
}

function dayLabelForTimezone(iso: string, timezone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Invalid date";
  let fmt = _dsLabelFmtCache.get(timezone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short", month: "short", day: "numeric" });
    _dsLabelFmtCache.set(timezone, fmt);
  }
  return fmt.format(d);
}

function isWithinRange(
  iso: string | null | undefined,
  rangeId: string,
): boolean {
  if (!iso) return false;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return false;

  const custom = parseCustomRange(rangeId);
  if (custom) return ts >= custom.from && ts <= custom.to;

  const now = Date.now();
  const windowMs =
    rangeId === "1h"
      ? 60 * 60 * 1000
      : rangeId === "7d"
        ? 7 * 24 * 60 * 60 * 1000
        : rangeId === "30d"
          ? 30 * 24 * 60 * 60 * 1000
          : 24 * 60 * 60 * 1000;
  return ts >= now - windowMs && ts <= now;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "-";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "-";
  const deltaMs = Date.now() - ts;
  const mins = Math.floor(deltaMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function exportRowsCsv(
  _rows: Array<Record<string, string | number | boolean | null | undefined>>,
  _fileName: string,
) {
  // No-op in design-system static demos.
}

async function exportRowsXlsx(
  _rows: Array<Record<string, string | number | boolean | null | undefined>>,
  _fileName: string,
): Promise<void> {
  // No-op in design-system static demos.
}

/* ============================================ */
/* HANDSONTABLE DEMO SECTION                    */
/* ============================================ */

type DsHotRow = {
  serial: string;
  name: string;
  model: string;
  status: string;
  tags: string;
};

const DS_HOT_DATA: DsHotRow[] = [
  {
    serial: "Q2XX-1111-AAAA",
    name: "MX95 Edge",
    model: "MX95",
    status: "online",
    tags: "edge, primary",
  },
  {
    serial: "Q2XX-2222-BBBB",
    name: "MS425 Dist-1",
    model: "MS425",
    status: "online",
    tags: "distribution",
  },
  {
    serial: "Q2XX-3333-CCCC",
    name: "MS425 Dist-2",
    model: "MS425",
    status: "alerting",
    tags: "distribution",
  },
  {
    serial: "Q2XX-4444-DDDD",
    name: "MR46 Lobby AP",
    model: "MR46",
    status: "online",
    tags: "wireless, lobby",
  },
  {
    serial: "Q2XX-5555-EEEE",
    name: "MR46 Conference",
    model: "MR46",
    status: "offline",
    tags: "wireless, conference",
  },
];

const DS_HOT_COLUMN_CONFIG: OpusColumnConfig = (col) => {
  if (col === "serial" || col === "model") return { readOnly: true };
  if (col === "status")
    return { type: "dropdown", source: ["online", "alerting", "offline"] };
  return undefined;
};

function HandsontableSection() {
  return (
    <Section
      title="Handsontable"
      hint="OpusHandsontable — Cadenza-themed editable grid with change tracking, undo, filters, and CSV export."
      code={`import {
  OpusHandsontable,
  type OpusColumnConfig,
} from "@/components/ui/opus-handsontable";

type MyRow = { id: string; name: string; status: string };

const COLUMNS: OpusColumnConfig<MyRow>[] = [
  { key: "id",     header: "ID",     readOnly: true },
  { key: "name",   header: "Name" },
  { key: "status", header: "Status" },
];

<OpusHandsontable<MyRow>
  data={rows}
  idConfig="id"
  columnConfig={COLUMNS}
  exportFileName="my-export"
/>`}
    >
      <div className="flex w-full flex-col gap-8">
        {/* Demo 1: populated table */}
        <div className="flex flex-col gap-2">
          <SubLabel>
            With data — edit name / status / tags, then Ctrl+Z to revert
          </SubLabel>
          <OpusHandsontable<DsHotRow>
            data={DS_HOT_DATA}
            idConfig="serial"
            columnConfig={DS_HOT_COLUMN_CONFIG}
            exportFileName="devices-sample"
            height={260}
          />
          <HotTable
            data={[
              ["", "Tesla", "Volvo", "Toyota", "Ford"],
              ["2019", 10, 11, 12, 13],
              ["2020", 20, 11, 14, 13],
              ["2021", 30, 15, 12, 13],
            ]}
            rowHeaders={true}
            colHeaders={true}
            // dropdownMenu={true}
            dropdownMenu={[
              "filter_by_condition",
              "filter_by_value",
              "filter_action_bar",
            ]}
            contextMenu={false}
            filters={true}
            height="auto"
            autoWrapRow={true}
            autoWrapCol={true}
            licenseKey="non-commercial-and-evaluation" // for non-commercial use only
          />
          <p className="text-xs text-fg-mute">
            The <span className="font-mono text-violet">serial</span> column is
            read-only and used as the stable row identity.{" "}
            <span className="font-mono text-violet">model</span> is also
            read-only. Edit <em>name</em>, <em>status</em>, or <em>tags</em> —
            changed rows highlight in mint green. Undo with{" "}
            <kbd className="rounded border border-line px-1 font-mono text-[10px]">
              Ctrl+Z
            </kbd>{" "}
            to revert and watch the highlight disappear. Theme toggle works
            automatically.
          </p>
        </div>

        {/* Demo 2: empty data with defaultEmptyRows */}
        <div className="flex flex-col gap-2">
          <SubLabel>
            Empty data — 5 blank entry rows via{" "}
            <span className="text-violet">defaultEmptyRows</span>
          </SubLabel>
          {/* <OpusHandsontable<DsHotRow>
            data={emptyData}
            idConfig="serial"
            columnKeys={[...DS_HOT_COLUMN_KEYS]}
            defaultEmptyRows={5}
            columnConfig={DS_HOT_COLUMN_CONFIG}
            exportFileName="devices-new"
            height={220}
          /> */}
          <p className="text-xs text-fg-mute">
            When <span className="font-mono text-violet">data</span> is empty,{" "}
            <span className="font-mono text-violet">defaultEmptyRows</span>{" "}
            generates blank editable rows using the schema from{" "}
            <span className="font-mono text-violet">columnKeys</span>. Edit a
            cell to see the green highlight; clear it back to empty to remove
            it.
          </p>
        </div>
      </div>
    </Section>
  );
}


const throughputRows = [
  {
    t: "09:00",
    iso: "2026-01-01T09:00:00Z",
    Received: 9_000_000,
    Sent: 2_000_000,
    Total: 11_000_000,
  },
  {
    t: "10:00",
    iso: "2026-01-01T10:00:00Z",
    Received: 12_000_000,
    Sent: 3_000_000,
    Total: 15_000_000,
  },
  {
    t: "11:00",
    iso: "2026-01-01T11:00:00Z",
    Received: 16_000_000,
    Sent: 4_000_000,
    Total: 20_000_000,
  },
  {
    t: "12:00",
    iso: "2026-01-01T12:00:00Z",
    Received: 22_000_000,
    Sent: 7_000_000,
    Total: 29_000_000,
  },
];

const dsFirmwareUpgrades = [
  {
    upgradeId: "fw-01",
    networkId: "N_1001",
    networkName: "Washington Central",
    productTypes: ["wireless", "switch"],
    status: "scheduled",
    phase: "upcoming",
    fromVersion: "MR 30.2",
    toVersion: "MR 30.5",
    scheduledAt: "2026-01-02T04:00:00Z",
    completedAt: null,
    timezone: "America/New_York",
  },
  {
    upgradeId: "fw-02",
    networkId: "N_1002",
    networkName: "New York Hub",
    productTypes: ["appliance"],
    status: "completed",
    phase: "completed",
    fromVersion: "MX 18.107",
    toVersion: "MX 18.211",
    scheduledAt: "2026-01-01T03:00:00Z",
    completedAt: "2026-01-01T03:18:00Z",
    timezone: "America/New_York",
  },
] as unknown as FirmwareUpgrade[];

const dsAlertHistory = [
  {
    occurredAt: "2026-01-01T09:10:00Z",
    alertType: "WAN latency high",
    alertTypeId: "wan_latency_high",
    deviceSerial: "Q2XX-AAA1-1111",
    emailSentAt: "2026-01-01T09:10:20Z",
    pushSentAt: "2026-01-01T09:10:25Z",
    smsSentAt: null,
    webhookSentAt: null,
  },
  {
    occurredAt: "2026-01-01T10:05:00Z",
    alertType: "Gateway DNS unreachable",
    alertTypeId: "dns_unreachable",
    deviceSerial: "Q2XX-BBB2-2222",
    emailSentAt: "2026-01-01T10:05:10Z",
    pushSentAt: null,
    smsSentAt: null,
    webhookSentAt: "2026-01-01T10:05:11Z",
  },
] as unknown as AlertHistoryRow[];

const dsAssuranceAlerts = [
  {
    id: "health-01",
    title: "High packet loss on WAN 1",
    type: "wan.packet_loss",
    severity: "critical",
    deviceName: "MX95 Edge",
    deviceSerial: "Q2KN-9X4P-AAAA",
    startedAt: "2026-01-01T08:55:00Z",
  },
  {
    id: "health-02",
    title: "AP retry rate elevated",
    type: "wireless.retries",
    severity: "warning",
    deviceName: "MR46 Lobby",
    deviceSerial: "Q2KN-1A2B-CCCC",
    startedAt: "2026-01-01T09:40:00Z",
  },
] as unknown as AssuranceAlert[];

const dsWirelessFailedConnections = [
  {
    ts: "2026-01-01T10:12:00Z",
    type: "auth_failure",
    failureStep: "802.1X",
    clientMac: "AA:BB:CC:DD:EE:01",
    ssidNumber: "CorpWiFi",
    vlan: "120",
  },
  {
    ts: "2026-01-01T10:31:00Z",
    type: "dhcp_timeout",
    failureStep: "DHCP",
    clientMac: "AA:BB:CC:DD:EE:02",
    ssidNumber: "GuestWiFi",
    vlan: "220",
  },
] as unknown as WirelessFailedConnection[];

const dsConnectedClients = [
  {
    id: "client-1",
    name: "android-e818-lobby-panel",
    mac: "AA:12:34:56:78:90",
    manufacturer: "Samsung",
    os: "Android",
    online: true,
    ip: "10.20.1.24",
    ssid: "CorpWiFi",
    vlanOrPort: "120",
    lastSeen: "2026-01-01T11:56:00Z",
    bytesDown: 730_000_000,
    bytesUp: 115_000_000,
    connectedDeviceName: "MS425 Core",
  },
  {
    id: "client-2",
    name: "Front Desk Printer",
    mac: "AA:12:34:56:78:91",
    manufacturer: "HP",
    os: "Embedded",
    online: true,
    ip: "10.20.3.45",
    ssid: "wired",
    vlanOrPort: "Port 12",
    lastSeen: "2026-01-01T11:48:00Z",
    bytesDown: 85_000_000,
    bytesUp: 23_000_000,
    connectedDeviceName: "MS250 Access",
  },
] as unknown as ConnectedClient[];

const dsDevices = [
  {
    serial: "Q2XX-1111-AAAA",
    name: "MX95 Edge",
    model: "MX95",
    status: "online",
    macAddress: "00:11:22:33:44:55",
    lanIp: "10.20.0.1",
    publicIp: "203.0.113.10",
    address: "1200 Cadenza Ave, Seattle",
    tags: ["edge", "primary"],
    lastSeen: "2026-01-01T11:59:00Z",
  },
  {
    serial: "Q2XX-2222-BBBB",
    name: "MR46 Lobby",
    model: "MR46",
    status: "alerting",
    macAddress: "00:11:22:33:44:66",
    lanIp: "10.20.1.11",
    publicIp: null,
    address: "",
    tags: ["wireless", "lobby"],
    lastSeen: "2026-01-01T11:45:00Z",
  },
] as unknown as SiteDevice[];

const dsTopologyNodes: Node[] = [
  {
    id: "mx-core",
    type: "default",
    position: { x: 40, y: 180 },
    data: {
      device: {
        serial: "Q2XX-1111-AAAA",
        name: "MX95 Edge",
        model: "MX95",
        status: "online",
        vendor: "meraki",
        ipAddress: "10.20.0.1",
        mac: "00:11:22:33:44:55",
        tags: ["edge", "primary"],
      },
    },
  },
  {
    id: "ms-dist-1",
    type: "default",
    position: { x: 320, y: 100 },
    data: {
      device: {
        serial: "Q2XX-2222-BBBB",
        name: "MS425 Dist-1",
        model: "MS425",
        status: "online",
        vendor: "meraki",
        ipAddress: "10.20.10.2",
        mac: "00:11:22:33:44:66",
        tags: ["distribution"],
      },
    },
  },
  {
    id: "ms-dist-2",
    type: "default",
    position: { x: 320, y: 260 },
    data: {
      device: {
        serial: "Q2XX-3333-CCCC",
        name: "MS425 Dist-2",
        model: "MS425",
        status: "alerting",
        vendor: "meraki",
        ipAddress: "10.20.10.3",
        mac: "00:11:22:33:44:77",
        tags: ["distribution"],
      },
    },
  },
  {
    id: "mr-lobby",
    type: "default",
    position: { x: 620, y: 60 },
    data: {
      device: {
        serial: "Q2XX-4444-DDDD",
        name: "MR46 Lobby AP",
        model: "MR46",
        status: "online",
        vendor: "meraki",
        ipAddress: "10.20.20.11",
        mac: "00:11:22:33:44:88",
        tags: ["wireless", "lobby"],
      },
    },
  },
  {
    id: "mr-conference",
    type: "default",
    position: { x: 620, y: 180 },
    data: {
      device: {
        serial: "Q2XX-5555-EEEE",
        name: "MR46 Conference AP",
        model: "MR46",
        status: "offline",
        vendor: "meraki",
        ipAddress: "10.20.20.12",
        mac: "00:11:22:33:44:99",
        tags: ["wireless", "conference"],
      },
    },
  },
  {
    id: "camera-nvr",
    type: "default",
    position: { x: 620, y: 300 },
    data: {
      device: {
        serial: "Q2XX-6666-FFFF",
        name: "NVR Camera Stack",
        model: "Camera Controller",
        status: "unknown",
        vendor: "meraki",
        ipAddress: "10.20.30.30",
        mac: "00:11:22:33:44:AA",
        tags: ["security"],
      },
    },
  },
];

const dsTopologyEdges: Edge[] = [
  {
    id: "e1",
    source: "mx-core",
    target: "ms-dist-1",
    animated: false,
    data: { sourcePort: "1/1", targetPort: "48" },
  },
  {
    id: "e2",
    source: "mx-core",
    target: "ms-dist-2",
    animated: true,
    data: { sourcePort: "1/2", targetPort: "48" },
    style: { stroke: "rgb(var(--peach))", strokeWidth: 2 },
  },
  {
    id: "e3",
    source: "ms-dist-1",
    target: "mr-lobby",
    data: { sourcePort: "18", targetPort: "0" },
  },
  {
    id: "e4",
    source: "ms-dist-1",
    target: "mr-conference",
    data: { sourcePort: "21", targetPort: "0" },
  },
  {
    id: "e5",
    source: "ms-dist-2",
    target: "camera-nvr",
    data: { sourcePort: "9", targetPort: "eth0" },
  },
];

const dsTopologyDetailsBySerial: Record<string, TopologyExplorerNodeDetails> =
  {
    "Q2XX-2222-BBBB": {
      serial: "Q2XX-2222-BBBB",
      name: "MS425 Dist-1",
      model: "MS425",
      productType: "switch",
      ports: [
        {
          portId: "1",
          name: "Uplink Core",
          status: "connected",
          speed: "10G",
        },
        {
          portId: "18",
          name: "MR46 Lobby AP",
          status: "connected",
          speed: "1G",
          clientCount: 23,
        },
        {
          portId: "21",
          name: "MR46 Conference AP",
          status: "connected",
          speed: "1G",
          clientCount: 9,
        },
        {
          portId: "24",
          name: "Reception Desk",
          status: "disconnected",
          speed: "1G",
        },
        {
          portId: "48",
          name: "MX95 Edge",
          status: "connected",
          speed: "10G",
        },
      ],
    },
    "Q2XX-3333-CCCC": {
      serial: "Q2XX-3333-CCCC",
      name: "MS425 Dist-2",
      model: "MS425",
      productType: "switch",
      ports: [
        {
          portId: "1",
          name: "Uplink Core",
          status: "connected",
          speed: "10G",
        },
        {
          portId: "9",
          name: "NVR Camera Stack",
          status: "connected",
          speed: "1G",
          clientCount: 6,
        },
        {
          portId: "12",
          name: "Storeroom",
          status: "disconnected",
          speed: "1G",
        },
        {
          portId: "48",
          name: "MX95 Edge",
          status: "connected",
          speed: "10G",
        },
      ],
    },
    "Q2XX-4444-DDDD": {
      serial: "Q2XX-4444-DDDD",
      name: "MR46 Lobby AP",
      model: "MR46",
      productType: "wireless",
      clients: [
        {
          id: "lobby-1",
          description: "Guest-iPhone-21",
          mac: "AA:11:22:33:44:55",
          ip: "10.20.41.21",
          ssid: "Guest",
          switchport: "18",
          vlan: 140,
          manufacturer: "Apple",
        },
        {
          id: "lobby-2",
          description: "POS-Terminal-3",
          mac: "AA:11:22:33:44:56",
          ip: "10.20.52.13",
          ssid: "Operations",
          switchport: "18",
          vlan: 210,
          manufacturer: "Ingenico",
        },
        {
          id: "lobby-3",
          description: "Lobby-TV",
          mac: "AA:11:22:33:44:57",
          ip: "10.20.60.8",
          ssid: "Media",
          switchport: "18",
          vlan: 260,
          manufacturer: "Samsung",
        },
      ],
    },
    "Q2XX-5555-EEEE": {
      serial: "Q2XX-5555-EEEE",
      name: "MR46 Conference AP",
      model: "MR46",
      productType: "wireless",
      clients: [
        {
          id: "conf-1",
          description: "MeetingRoom-Bar-1",
          mac: "AA:11:22:33:44:66",
          ip: "10.20.41.72",
          ssid: "Guest",
          switchport: "21",
          vlan: 140,
          manufacturer: "Bose",
        },
        {
          id: "conf-2",
          description: "Presenter-Laptop",
          mac: "AA:11:22:33:44:67",
          ip: "10.20.41.73",
          ssid: "Guest",
          switchport: "21",
          vlan: 140,
          manufacturer: "Dell",
        },
      ],
    },
  };


function DsPrimitivesSection({ name, setName }: { name: string; setName: (v: string) => void }) {
  return (
    <>
      {/* Color tokens */}
      <Section
        title="Color tokens"
        hint="Theme via [data-theme] on <html>. Toggle in the top bar."
        className="border-t-0 pt-0"
        code={`/* CSS custom property — use via Tailwind token or inline style */
.my-element {
  background: rgb(var(--surface));
  color: rgb(var(--fg));
  border-color: rgb(var(--line));
}

/* Tailwind (configured via theme.extend.colors) */
<div className="bg-surface text-fg border-line" />`}
      >
        <Swatch name="bg-0" varName="--bg-0" />
        <Swatch name="bg-1" varName="--bg-1" />
        <Swatch name="surface" varName="--surface" />
        <Swatch name="surface-2" varName="--surface-2" />
        <Swatch name="surface-tint" varName="--surface-tint" />
        <Swatch name="line" varName="--line" />
        <Swatch name="line-strong" varName="--line-strong" />
        <Swatch name="fg" varName="--fg" />
        <Swatch name="fg-dim" varName="--fg-dim" />
        <Swatch name="fg-mute" varName="--fg-mute" />
        <Swatch name="violet" varName="--violet" />
        <Swatch name="cyan" varName="--cyan" />
        <Swatch name="mint" varName="--mint" />
        <Swatch name="peach" varName="--peach" />
        <Swatch name="rose" varName="--rose" />
      </Section>

      {/* Typography */}
      <Section
        title="Typography"
        hint="Manrope (sans) + JetBrains Mono (code/IDs/KPIs)."
        code={`/* Page title */
<h1 className="text-3xl font-extrabold tracking-tight text-aurora">Title</h1>

/* Section heading */
<h2 className="text-[24px] font-bold tracking-tight text-fg">Heading</h2>

/* Body */
<p className="text-base text-fg-dim">Body copy</p>

/* Mono / IDs / KPIs */
<span className="font-mono text-xs text-fg-mute">SN · Q2KN-9X4P-AAAA</span>`}
      >
        <div className="flex w-full flex-col gap-1.5">
          <div className="text-aurora text-[40px] font-extrabold leading-none tracking-tight">
            Cadenza lives the life
          </div>
          <div className="text-[24px] font-bold tracking-tight text-fg">
            Operations overview
          </div>
          <div className="text-base text-fg-dim">
            Body text — Opus runs across 1,284 devices and 46 sites worldwide.
            Sentences read in Manrope at 14–16 px.
          </div>
          <div className="font-mono text-xs text-fg-mute">
            SN · Q2KN-9X4P-AAAA — JetBrains Mono 12px
          </div>
        </div>
      </Section>

      {/* Buttons */}
      <Section
        title="Buttons"
        hint="Variants × sizes. Use `asChild` to wrap <Link>."
        code={`import { Button } from "@/components/ui/button";

<Button>Primary</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="subtle">Subtle</Button>
<Button variant="danger">Danger</Button>
<Button variant="link">Link button</Button>

<Button size="sm">Small</Button>
<Button size="md">Medium</Button>
<Button size="lg">Large</Button>
<Button disabled>Disabled</Button>

/* Wrap a router link */
<Button asChild variant="ghost">
  <Link to="/somewhere">Navigate</Link>
</Button>`}
      >
        <Button>Primary</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="subtle">Subtle</Button>
        <Button variant="danger">Danger</Button>
        <Button variant="link">Link button</Button>
        <span className="mx-2 h-8 w-px bg-line" />
        <Button size="sm">sm</Button>
        <Button size="md">md</Button>
        <Button size="lg">lg</Button>
        <Button disabled>disabled</Button>
      </Section>

      {/* Inputs */}
      <Section
        title="Inputs"
        code={`import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

<div className="flex flex-col gap-1.5">
  <Label htmlFor="field">Site code</Label>
  <Input id="field" placeholder="Enter site code" />
</div>

<Input placeholder="Disabled" disabled />
<Input placeholder="Invalid value" invalid />`}
      >
        <div className="flex w-full max-w-md flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ds-site">Site code</Label>
            <Input
              id="ds-site"
              placeholder="Enter site code (e.g. SLC-LAB)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <Input placeholder="Disabled" disabled />
          <Input
            placeholder="Invalid value"
            invalid
            defaultValue="not-a-vlan"
          />
        </div>
      </Section>

      {/* Stepper */}
      <Section
        title="Stepper"
        hint="Multi-step flows — debranding, config-push wizard, onboarding."
        code={`import { Stepper } from "@/components/ui/stepper";

<Stepper
  active={1}
  steps={[
    { id: "backup",  title: "Backup",  hint: "Snapshot configuration" },
    { id: "migrate", title: "Migrate", hint: "Move to new org" },
    { id: "restore", title: "Restore", hint: "Apply to target" },
  ]}
/>`}
      >
        <div className="w-full">
          <Stepper
            active={1}
            steps={[
              { id: "a", title: "Backup", hint: "Snapshot configuration" },
              { id: "b", title: "Migrate", hint: "Move to new org" },
              { id: "c", title: "Restore", hint: "Apply to target" },
            ]}
          />
        </div>
      </Section>

      {/* Progress */}
      <Section
        title="Progress"
        hint="Determinate (a value) or indeterminate (sliding)."
        code={`import { Progress } from "@/components/ui/progress";

/* Determinate */
<Progress value={64} label="Networks scanned" hint="64 of 100 endpoints" />

/* Indeterminate (no value prop) */
<Progress label="Talking to Meraki…" />`}
      >
        <div className="flex w-full flex-col gap-5">
          <Progress
            value={64}
            label="Backup · networks scanned"
            hint="64 of 100 endpoints"
          />
          <Progress label="Indeterminate · talking to Meraki" />
        </div>
      </Section>

    </>
  );
}

function DsUiSection() {
  return (
    <>
      {/* Scrollbar styling */}
      <Section
        title="Scrollbar styling"
        hint="Minimal scrollbar — subtle by default, more visible on hover. Positioned at right edge with content padding."
        code={`/* Minimal scrollbar pattern — 6px width, visible with subtle color */
<div className="
  h-full
  overflow-y-auto
  [&::-webkit-scrollbar]:w-[6px]
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-thumb]:bg-line/30
  hover:[&::-webkit-scrollbar-thumb]:bg-line/50
  [&::-webkit-scrollbar-track]:bg-transparent
">
  <div className="pr-2">
    {/* Scrollable content with spacing from scrollbar */}
  </div>
</div>`}
      >
        <Card className="h-[180px] overflow-y-auto [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-line/30 hover:[&::-webkit-scrollbar-thumb]:bg-line/50 [&::-webkit-scrollbar-track]:bg-transparent">
          <div className="pr-2 space-y-3 text-sm text-fg-dim">
            <p>
              Hover over this card to reveal the enhanced scrollbar on the right
              edge.
            </p>
            <p>
              The scrollbar is 6px wide positioned at the very right edge, with
              content having 8px (pr-2) padding.
            </p>
            <p>
              This pattern keeps the UI clean while maintaining scroll
              affordance when needed.
            </p>
            <p>
              Used throughout the application in sidebars, dropdowns, and
              scrollable containers.
            </p>
            <p>
              The thumb has line/30 opacity by default and increases to line/50
              on hover.
            </p>
            <p>
              This creates a balanced scrolling experience that doesn't compete
              with content.
            </p>
            <p>
              Additional lines to demonstrate scrolling behavior in a
              constrained space.
            </p>
            <p>
              Notice how the scrollbar seamlessly integrates with the Cadenza
              design system.
            </p>
            <p>The rounded-full class gives the thumb smooth, modern edges.</p>
            <p>
              Scroll to the bottom to see the full effect of the minimal styling
              approach.
            </p>
          </div>
        </Card>
      </Section>

      {/* Dropdown menu */}
      <Section
        title="Dropdown menu"
        hint="Used by env switcher, row actions, account menu."
        code={`import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

<DropdownMenu>
  <DropdownMenuTrigger asChild>
    <Button variant="ghost">Open menu</Button>
  </DropdownMenuTrigger>
  <DropdownMenuContent>
    <DropdownMenuLabel>Actions</DropdownMenuLabel>
    <DropdownMenuSeparator />
    <DropdownMenuItem>Push config</DropdownMenuItem>
    <DropdownMenuItem className="text-rose data-[highlighted]:text-rose">
      Quarantine device
    </DropdownMenuItem>
  </DropdownMenuContent>
</DropdownMenu>`}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost">Open menu</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>Push config</DropdownMenuItem>
            <DropdownMenuItem>View diff</DropdownMenuItem>
            <DropdownMenuItem>Open in Meraki dashboard</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-rose data-[highlighted]:text-rose">
              Quarantine device
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Section>

      {/* Badges */}
      <Section
        title="Badges"
        code={`import { Badge } from "@/components/ui/badge";

/* Status variants with dot indicator */
<Badge variant="ok" dot>online</Badge>
<Badge variant="warn" dot>retrying</Badge>
<Badge variant="err" dot>offline</Badge>
<Badge variant="info">scheduled</Badge>
<Badge variant="neutral">draft</Badge>

/* Larger size for environment labels */
<Badge size="md" variant="ok">PROD</Badge>
<Badge size="md" variant="warn">STAGING</Badge>`}
      >
        <Badge variant="ok" dot>
          online
        </Badge>
        <Badge variant="warn" dot>
          retrying
        </Badge>
        <Badge variant="err" dot>
          offline
        </Badge>
        <Badge variant="info">scheduled</Badge>
        <Badge variant="neutral">draft</Badge>
        <span className="mx-2 h-6 w-px bg-line" />
        <Badge size="md" variant="ok">
          PROD
        </Badge>
        <Badge size="md" variant="warn">
          STAGING
        </Badge>
      </Section>

      {/* Avatars */}
      <Section
        title="Avatars"
        code={`import { Avatar } from "@/components/ui/avatar";

<Avatar size="sm" initials="ED" />
<Avatar size="md" initials="JD" />
<Avatar size="lg" initials="KK" />`}
      >
        <Avatar size="sm" initials="ED" />
        <Avatar size="md" initials="JD" />
        <Avatar size="lg" initials="KK" />
      </Section>

      {/* KPI cards */}
      <Section
        title="KPI cards"
        hint="Big number + trend pill. Used on dashboards."
        code={`import { KpiCard } from "@/components/ui/kpi-card";
import { Server } from "lucide-react";

<KpiCard
  label="Devices online"
  value="1,284"
  trend="up"
  trendLabel="+18 vs 24h"
  icon={<Server size={14} />}
/>
<KpiCard label="Workflows" value="12" trend="flat" trendLabel="unchanged" />`}
      >
        <div className="grid w-full grid-cols-4 gap-3.5">
          <KpiCard
            label="Devices online"
            value="1,284"
            trend="up"
            trendLabel="+18 vs 24h"
            icon={<Server size={14} />}
          />
          <KpiCard
            label="Configs queued"
            value="37"
            trend="down"
            trendLabel="−4 vs 1h"
            icon={<Layers size={14} />}
          />
          <KpiCard
            label="Errors 24h"
            value="2"
            trend="down"
            trendLabel="−9 vs 24h"
            icon={<AlertCircle size={14} />}
          />
          <KpiCard
            label="Workflows"
            value="12"
            trend="flat"
            trendLabel="unchanged"
          />
        </div>
      </Section>

      <Section
        title="Rolling number"
        hint="Digit-resolution animation from highest place to lowest."
        code={`import { RollingNumber } from "@/components/ui/rolling-number";

/* Integer with locale formatting */
<RollingNumber value={1284} format={(v) => Math.round(v).toLocaleString()} />

/* Throughput */
<RollingNumber
  value={982_000_000}
  format={(v) => \`\${(v / 1_000_000).toFixed(0)} Mb/s\`}
/>

/* Capacity */
<RollingNumber
  value={1_536_000_000}
  format={(v) => \`\${(v / 1_000_000_000).toFixed(2)} GB\`}
/>`}
      >
        <div className="flex w-full flex-wrap items-center gap-6">
          <div className="flex flex-col gap-1">
            <SubLabel>Integer</SubLabel>
            <span className="text-3xl font-extrabold text-aurora">
              <RollingNumber
                value={1284}
                format={(v) => Math.round(v).toLocaleString()}
              />
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <SubLabel>Throughput</SubLabel>
            <span className="text-3xl font-extrabold text-aurora">
              <RollingNumber
                value={982_000_000}
                format={(v) => `${(v / 1_000_000).toFixed(0)} Mb/s`}
              />
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <SubLabel>Capacity</SubLabel>
            <span className="text-3xl font-extrabold text-aurora">
              <RollingNumber
                value={1_536_000_000}
                format={(v) => `${(v / 1_000_000_000).toFixed(2)} GB`}
              />
            </span>
          </div>
        </div>
      </Section>

    </>
  );
}

function DsDashboardSectionCharts({ sampleSite, setSampleSite, sampleRange, setSampleRange }: { sampleSite: string; setSampleSite: (v: string) => void; sampleRange: string; setSampleRange: (v: string) => void }) {
  return (
    <>
          <SubLabel>Controls</SubLabel>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_220px]">
            <SitePickerInline
              value={sampleSite}
              sites={[
                {
                  slug: "wasci",
                  code: "WASCI",
                  innCode: "WAS001",
                  navisionCode: "NV-1209",
                  friendlyName: "Washington Central",
                },
                {
                  slug: "nyc-hub",
                  code: "NYCHB",
                  innCode: "NYC210",
                  navisionCode: "NV-1301",
                  friendlyName: "New York Hub",
                },
              ]}
              onChange={setSampleSite}
            />
            <RangePicker
              value={sampleRange}
              onChange={setSampleRange}
              options={DS_RANGE_OPTIONS}
              rangeLabel={rangeLabel}
              parseCustomRange={parseCustomRange}
              customRangePrefix={DS_CUSTOM_RANGE_PREFIX}
            />
          </div>

          <SubLabel>Summary panel states</SubLabel>
          <div className="grid w-full grid-cols-1 gap-3 xl:grid-cols-3">
            <DashboardAiSummaryPanel
              showResummarize={false}
              summarizeReady={false}
              summarizing={false}
              gatheringSummaryData
              computingSummary={false}
              summaryError={null}
              summaryData={null}
              onSummarize={() => {}}
            />
            <DashboardAiSummaryPanel
              showResummarize={false}
              summarizeReady
              summarizing={false}
              gatheringSummaryData={false}
              computingSummary={false}
              summaryError={"Sample error while generating summary"}
              summaryData={null}
              onSummarize={() => {}}
            />
            <DashboardAiSummaryPanel
              showResummarize
              summarizeReady
              summarizing={false}
              gatheringSummaryData={false}
              computingSummary={false}
              summaryError={null}
              summaryData={{
                summary: "Network healthy with two actionable warnings.",
                details:
                  "1) Review WAN uplink jitter trend.\n2) Schedule firmware upgrade for switch ring.",
                elapsedMs: 472,
                model: "gpt-5.3-codex",
              }}
              onSummarize={() => {}}
            />
          </div>

          <SubLabel>Metrics and charts</SubLabel>
          <div className="grid w-full grid-cols-1 gap-3 lg:grid-cols-3">
            <DeviceClassCard
              rollup={{
                label: "Appliances",
                total: 7,
                byStatus: { online: 6, alerting: 1, offline: 0, dormant: 0 },
              }}
            />
            <StatTile
              label="Peak Throughput"
              icon={<Layers size={14} />}
              numericValue={29_000_000}
              formatFn={fmtBps}
              hint="rx + tx in selected range"
            />
            <div className="rounded-xl border border-line bg-surface p-3">
              <div style={{ width: "100%", height: 200 }}>
                <ThroughputChartSurface
                  idPrefix="design-system-throughput"
                  data={throughputRows}
                  yAxisMax={26_000_000}
                  visibleSeries={{ Received: true, Sent: true }}
                  rxLimitBps={20_000_000}
                  txLimitBps={8_000_000}
                  sharedLimitBps={20_000_000}
                  valueFormatter={fmtBps}
                />
              </div>
              <div className="mt-2 flex flex-col gap-2">
                <ChartStat
                  color="bg-cyan"
                  label="Downstream"
                  avg={14_750_000}
                  max={22_000_000}
                  min={9_000_000}
                  limit={20_000_000}
                  showPercent
                />
                <ChartStat
                  color="bg-peach"
                  label="Upstream"
                  avg={4_000_000}
                  max={7_000_000}
                  min={2_000_000}
                  limit={8_000_000}
                  showPercent
                />
              </div>
            </div>
          </div>

          <SubLabel>Appliance rail</SubLabel>
          <ApplianceStatusRail
            uplinks={[
              {
                label: "WAN 1",
                model: "MX95",
                state: "active",
                publicIp: "203.0.113.10",
                lanIp: "10.0.0.1",
                gateway: "10.0.0.254",
                primaryDns: "8.8.8.8",
                secondaryDns: "1.1.1.1",
              },
              {
                label: "WAN 2",
                model: "MX95",
                state: "ready",
                publicIp: "198.51.100.14",
                lanIp: "10.0.1.1",
                gateway: "10.0.1.254",
                primaryDns: "9.9.9.9",
                secondaryDns: "149.112.112.112",
              },
            ]}
            siteCode="WASCI"
            friendlyName="Washington Central"
          />

    </>
  );
}

type AlertsTabKey = "networkEvents" | "health" | "connections";

function DsDashboardSectionTables({ sampleRange, sampleAlertsTab, setSampleAlertsTab }: { sampleRange: string; sampleAlertsTab: AlertsTabKey; setSampleAlertsTab: (v: AlertsTabKey) => void }) {
  return (
    <>
          <SubLabel>Firmware upgrades</SubLabel>
          <FirmwareUpgradesSection
            upgrades={dsFirmwareUpgrades}
            range={sampleRange}
            refreshing={false}
            onRefresh={() => {}}
            rangeOptions={DS_RANGE_OPTIONS}
            isWithinRange={isWithinRange}
            fmtDateTime={fmtDateTime}
            dayKeyForTimezone={dayKeyForTimezone}
            dayLabelForTimezone={dayLabelForTimezone}
            exportRowsCsv={exportRowsCsv}
            exportRowsXlsx={exportRowsXlsx}
          />

          <SubLabel>Alerts</SubLabel>
          <AlertsSection
            alerts={dsAlertHistory}
            assuranceAlerts={dsAssuranceAlerts}
            wirelessFailedConnections={dsWirelessFailedConnections}
            activeTab={sampleAlertsTab}
            onTabChange={setSampleAlertsTab}
            range={sampleRange}
            loadingNetworkEvents={false}
            loadingHealth={false}
            loadingConnections={false}
            onRefreshNetworkEvents={() => {}}
            onRefreshHealth={() => {}}
            onRefreshConnections={() => {}}
            rangeLabel={rangeLabel}
            isWithinRange={isWithinRange}
            fmtDateTime={fmtDateTime}
          />

          <SubLabel>Connected clients</SubLabel>
          <ConnectedClientsTable
            clients={dsConnectedClients}
            totalShown={dsConnectedClients.length}
            range={sampleRange}
            refreshing={false}
            onRefresh={() => {}}
            rangeLabel={rangeLabel}
            isWithinRange={isWithinRange}
            timeAgo={timeAgo}
            fmtBytes={fmtBytes}
            exportRowsCsv={exportRowsCsv}
            exportRowsXlsx={exportRowsXlsx}
          />

          <SubLabel>Devices</SubLabel>
          <DevicesTable
            devices={dsDevices}
            embedded
            enableExport={false}
            timeAgo={timeAgo}
          />

          <SubLabel>State variants (loading + empty)</SubLabel>
          <div className="grid w-full grid-cols-1 gap-4 2xl:grid-cols-2">
            <DemoVariantCard label="Firmware upgrades · refreshing">
              <FirmwareUpgradesSection
                upgrades={dsFirmwareUpgrades}
                range={sampleRange}
                refreshing
                onRefresh={() => {}}
                rangeOptions={DS_RANGE_OPTIONS}
                isWithinRange={isWithinRange}
                fmtDateTime={fmtDateTime}
                dayKeyForTimezone={dayKeyForTimezone}
                dayLabelForTimezone={dayLabelForTimezone}
                exportRowsCsv={exportRowsCsv}
                exportRowsXlsx={exportRowsXlsx}
              />
            </DemoVariantCard>

            <DemoVariantCard label="Firmware upgrades · empty">
              <FirmwareUpgradesSection
                upgrades={[]}
                range={sampleRange}
                refreshing={false}
                onRefresh={() => {}}
                rangeOptions={DS_RANGE_OPTIONS}
                isWithinRange={isWithinRange}
                fmtDateTime={fmtDateTime}
                dayKeyForTimezone={dayKeyForTimezone}
                dayLabelForTimezone={dayLabelForTimezone}
                exportRowsCsv={exportRowsCsv}
                exportRowsXlsx={exportRowsXlsx}
              />
            </DemoVariantCard>

            <DemoVariantCard label="Alerts · loading">
              <AlertsSection
                alerts={dsAlertHistory}
                assuranceAlerts={dsAssuranceAlerts}
                wirelessFailedConnections={dsWirelessFailedConnections}
                activeTab="networkEvents"
                onTabChange={() => {}}
                range={sampleRange}
                loadingNetworkEvents
                loadingHealth={false}
                loadingConnections={false}
                onRefreshNetworkEvents={() => {}}
                onRefreshHealth={() => {}}
                onRefreshConnections={() => {}}
                rangeLabel={rangeLabel}
                isWithinRange={isWithinRange}
                fmtDateTime={fmtDateTime}
              />
            </DemoVariantCard>

            <DemoVariantCard label="Alerts · empty">
              <AlertsSection
                alerts={[]}
                assuranceAlerts={[]}
                wirelessFailedConnections={[]}
                activeTab="connections"
                onTabChange={() => {}}
                range={sampleRange}
                loadingNetworkEvents={false}
                loadingHealth={false}
                loadingConnections={false}
                onRefreshNetworkEvents={() => {}}
                onRefreshHealth={() => {}}
                onRefreshConnections={() => {}}
                rangeLabel={rangeLabel}
                isWithinRange={isWithinRange}
                fmtDateTime={fmtDateTime}
              />
            </DemoVariantCard>

            <DemoVariantCard label="Connected clients · refreshing">
              <ConnectedClientsTable
                clients={dsConnectedClients}
                totalShown={dsConnectedClients.length}
                range={sampleRange}
                refreshing
                onRefresh={() => {}}
                rangeLabel={rangeLabel}
                isWithinRange={isWithinRange}
                timeAgo={timeAgo}
                fmtBytes={fmtBytes}
                exportRowsCsv={exportRowsCsv}
                exportRowsXlsx={exportRowsXlsx}
              />
            </DemoVariantCard>

            <DemoVariantCard label="Connected clients · empty">
              <ConnectedClientsTable
                clients={[]}
                totalShown={0}
                range={sampleRange}
                refreshing={false}
                onRefresh={() => {}}
                rangeLabel={rangeLabel}
                isWithinRange={isWithinRange}
                timeAgo={timeAgo}
                fmtBytes={fmtBytes}
                exportRowsCsv={exportRowsCsv}
                exportRowsXlsx={exportRowsXlsx}
              />
            </DemoVariantCard>

            <DemoVariantCard label="Devices · loading shell">
              <TableSkeleton
                columns="1.4fr 120px 150px 120px"
                columnCount={4}
                rows={3}
              />
            </DemoVariantCard>

            <DemoVariantCard label="Devices · empty">
              <DevicesTable
                devices={[]}
                embedded
                enableExport={false}
                timeAgo={timeAgo}
              />
            </DemoVariantCard>
          </div>
    </>
  );
}

function DsDashboardSection({
  sampleSite,
  setSampleSite,
  sampleRange,
  setSampleRange,
  sampleAlertsTab,
  setSampleAlertsTab,
}: {
  sampleSite: string;
  setSampleSite: (v: string) => void;
  sampleRange: string;
  setSampleRange: (v: string) => void;
  sampleAlertsTab: AlertsTabKey;
  setSampleAlertsTab: (v: AlertsTabKey) => void;
}) {
  return (
    <Section
      title="Dashboard composites"
      hint="Extracted site-dashboard components shown with static sample props."
      code={`import { SitePickerInline } from "@/components/app/dashboard/site-picker-inline";
import { RangePicker } from "@/components/app/dashboard/range-picker";
import { DashboardAiSummaryPanel } from "@/components/app/dashboard/ai-summary-panel";
import { FirmwareUpgradesSection } from "@/components/app/dashboard/firmware-upgrades-section";
import { AlertsSection } from "@/components/app/dashboard/alerts-section";
import { ConnectedClientsTable } from "@/components/app/dashboard/connected-clients-table";
import { DevicesTable } from "@/components/app/dashboard/devices-table";
import { StatTile } from "@/components/app/dashboard/stat-tile";
import { DeviceClassCard } from "@/components/app/dashboard/device-class-card";
import { ApplianceStatusRail } from "@/components/app/dashboard/appliance-status-rail";

/* Refer to the dashboard page for full usage — components accept typed props
   and control their own loading/empty/error states internally. */`}
    >
      <div className="grid w-full grid-cols-1 gap-4">
        <DsDashboardSectionCharts
          sampleSite={sampleSite}
          setSampleSite={setSampleSite}
          sampleRange={sampleRange}
          setSampleRange={setSampleRange}
        />
        <DsDashboardSectionTables
          sampleRange={sampleRange}
          sampleAlertsTab={sampleAlertsTab}
          setSampleAlertsTab={setSampleAlertsTab}
        />
      </div>
    </Section>
  );
}

function DsAdvancedSection({ themeMode }: { themeMode: "light" | "dark" }) {
  return (
    <>
      <Section
        title="Topology viewer"
        hint="Reusable React Flow canvas extracted from the topology page."
        code={`import {
  TopologyExplorer,
  type TopologyExplorerNodeDetails,
} from "@/components/app/topology/topology-explorer";
import type { Node, Edge } from "@xyflow/react";

<TopologyExplorer
  nodes={nodes}          // Node[]
  edges={edges}          // Edge[]
  colorMode={themeMode}  // "light" | "dark"
  fitDep="my-page"
  height={420}
  emptyMessage="No devices found."
  filterLabel="Filters"
  getNodeDetails={(node) => detailsBySerial[node.data.device?.serial ?? ""]}
/>`}
      >
        <div className="grid w-full grid-cols-1 gap-3">
          <SubLabel>Interactive site topology demo</SubLabel>
          <TopologyExplorer
            nodes={dsTopologyNodes}
            edges={dsTopologyEdges}
            colorMode={themeMode}
            fitDep={`design-system-topology-${themeMode}`}
            height={420}
            emptyMessage="No topology sample nodes configured."
            filterLabel="Filters"
            getNodeDetails={(node) => {
              const serial =
                typeof (node.data as Record<string, unknown> | undefined)
                  ?.device === "object"
                  ? ((node.data as { device?: { serial?: string } }).device
                      ?.serial ?? "")
                  : "";
              return serial ? dsTopologyDetailsBySerial[serial] : undefined;
            }}
          />
        </div>
      </Section>

      {/* Handsontable */}
      <HandsontableSection />

      {/* Tabs */}
      <Section
        title="Tabs"
        code={`import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

<Tabs defaultValue="all">
  <TabsList>
    <TabsTrigger value="all">All</TabsTrigger>
    <TabsTrigger value="meraki">Meraki</TabsTrigger>
    <TabsTrigger value="aruba">Aruba</TabsTrigger>
  </TabsList>
  <TabsContent value="all">
    <div>All vendors selected.</div>
  </TabsContent>
</Tabs>`}
      >
        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="meraki">Meraki</TabsTrigger>
            <TabsTrigger value="aruba">Aruba</TabsTrigger>
            <TabsTrigger value="shasta">Shasta</TabsTrigger>
          </TabsList>
          <TabsContent value="all">
            <div className="text-sm text-fg-dim">All vendors selected.</div>
          </TabsContent>
        </Tabs>
      </Section>

      {/* Dialog */}
      <Section
        title="Dialog (modal)"
        code={`import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

<Dialog>
  <DialogTrigger asChild>
    <Button>Open dialog</Button>
  </DialogTrigger>
  <DialogContent>
    <DialogTitle>Confirm action</DialogTitle>
    <DialogDescription>This action is logged to the audit trail.</DialogDescription>
    <div className="mt-5 flex justify-end gap-2.5">
      <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
      <DialogClose asChild><Button>Confirm</Button></DialogClose>
    </div>
  </DialogContent>
</Dialog>`}
      >
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="primary">Open dialog</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogTitle>Push config to MX84-edge-01?</DialogTitle>
            <DialogDescription>
              Diff is 14 lines. Estimated push time 1.4s. This action is logged
              to the audit trail.
            </DialogDescription>
            <div className="mt-5 flex justify-end gap-2.5">
              <DialogClose asChild>
                <Button variant="ghost">Cancel</Button>
              </DialogClose>
              <DialogClose asChild>
                <Button variant="primary">Push config</Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      </Section>

      {/* Card composition */}
      <Section
        title="Card · composition"
        code={`import { Card, CardHeader, CardTitle, CardSub, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

<Card>
  <CardHeader>
    <div>
      <CardTitle>Workflow ready to run</CardTitle>
      <CardSub>Hotel cutover · Marriott PAR-09</CardSub>
    </div>
    <Badge variant="ok" dot>ready</Badge>
  </CardHeader>
  <CardContent>
    <p className="m-0 text-sm text-fg-dim">Description goes here.</p>
    <div className="mt-3.5 flex gap-2.5">
      <Button>Run workflow</Button>
      <Button variant="ghost">Inspect plan</Button>
    </div>
  </CardContent>
</Card>`}
      >
        <div className="w-full">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Workflow ready to run</CardTitle>
                <CardSub>Hotel cutover · Marriott PAR-09</CardSub>
              </div>
              <Badge variant="ok" dot>
                ready
              </Badge>
            </CardHeader>
            <CardContent>
              <p className="m-0 text-sm text-fg-dim">
                VLAN re-tag + AP firmware bump. 14 devices, dry-run complete.
              </p>
              <div className="mt-3.5 flex gap-2.5">
                <Button>Run workflow</Button>
                <Button variant="ghost">Inspect plan</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Section>
    </>
  );
}

function DsTableSection() {
  return (
    <>
      {/* Table */}
      <Section
        title="Table (grid)"
        code={`import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { Badge } from "@/components/ui/badge";

const COLS = "1.2fr 1fr 0.7fr 0.5fr";

<TableShell>
  <TableHeadRow columns={COLS}>
    <TableHeadCell>Site</TableHeadCell>
    <TableHeadCell>Device</TableHeadCell>
    <TableHeadCell>Status</TableHeadCell>
    <TableHeadCell>Latency</TableHeadCell>
  </TableHeadRow>
  <TableRow columns={COLS}>
    <TableCell><b className="font-semibold text-fg">SLC-LAB</b></TableCell>
    <TableCell><span className="font-mono text-xs">MX84-edge-01</span></TableCell>
    <TableCell><Badge variant="ok" dot>online</Badge></TableCell>
    <TableCell className="font-mono text-xs text-fg-dim">12 ms</TableCell>
  </TableRow>
</TableShell>`}
      >
        <div className="w-full">
          <TableShell>
            <TableHeadRow columns={SAMPLE_COLS}>
              <TableHeadCell>Site</TableHeadCell>
              <TableHeadCell>Device</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Latency</TableHeadCell>
            </TableHeadRow>
            <TableRow columns={SAMPLE_COLS}>
              <TableCell>
                <b className="font-semibold text-fg">SLC-LAB</b>
              </TableCell>
              <TableCell>
                <span className="font-mono text-xs text-fg-dim">
                  MX84-edge-01
                </span>
              </TableCell>
              <TableCell>
                <Badge variant="ok" dot>
                  online
                </Badge>
              </TableCell>
              <TableCell className="font-mono text-xs text-fg-dim">
                12 ms
              </TableCell>
            </TableRow>
            <TableRow columns={SAMPLE_COLS}>
              <TableCell>
                <b className="font-semibold text-fg">NYC-21</b>
              </TableCell>
              <TableCell>
                <span className="font-mono text-xs text-fg-dim">
                  MS425-core-08
                </span>
              </TableCell>
              <TableCell>
                <Badge variant="warn" dot>
                  retrying
                </Badge>
              </TableCell>
              <TableCell className="font-mono text-xs text-fg-dim">
                214 ms
              </TableCell>
            </TableRow>
            <TableRow columns={SAMPLE_COLS}>
              <TableCell>
                <b className="font-semibold text-fg">MIA-12</b>
              </TableCell>
              <TableCell>
                <span className="font-mono text-xs text-fg-dim">
                  shasta-gw-02
                </span>
              </TableCell>
              <TableCell>
                <Badge variant="err" dot>
                  offline
                </Badge>
              </TableCell>
              <TableCell className="font-mono text-xs text-fg-dim">
                — ms
              </TableCell>
            </TableRow>
          </TableShell>
        </div>
      </Section>

      {/* Skeletons */}
      <Section
        title="Skeletons"
        hint="Use layout-matching placeholders instead of generic Loading text."
        code={`import {
  Skeleton,
  TableSkeleton,
  CardGridSkeleton,
  FormSkeleton,
  PanelSkeleton,
} from "@/components/ui/skeleton";

/* Base shapes */
<Skeleton className="h-3 w-24" />          {/* line */}
<Skeleton variant="block" className="h-8 w-20" />  {/* block */}
<Skeleton variant="circle" className="h-8 w-8" />  {/* circle */}

/* Composite layouts */
<PanelSkeleton />
<FormSkeleton lines={3} />
<TableSkeleton columns="1.2fr 1fr 0.7fr" columnCount={3} rows={4} />
<CardGridSkeleton cards={2} />`}
      >
        <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <DemoVariantCard label="Base primitives">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-3 w-24" />
                <Skeleton variant="block" className="h-8 w-20" />
                <Skeleton variant="circle" className="h-8 w-8" />
              </div>
              <PanelSkeleton />
            </div>
          </DemoVariantCard>
          <DemoVariantCard label="Form shell">
            <FormSkeleton lines={3} />
          </DemoVariantCard>
        </div>
        <div className="grid w-full grid-cols-1 gap-4">
          <SubLabel>Table shell</SubLabel>
          <TableSkeleton columns={SAMPLE_COLS} columnCount={4} rows={3} />
        </div>
        <div className="grid w-full grid-cols-1 gap-4">
          <SubLabel>Card grid shell</SubLabel>
          <CardGridSkeleton cards={2} />
        </div>
      </Section>
    </>
  );
}

export function DesignSystemPage() {
  const themeMode = useTheme((s) => s.resolved);
  const [name, setName] = useState("");
  const [sampleSite, setSampleSite] = useState("wasci");
  const [sampleRange, setSampleRange] = useState("24h");
  const [sampleAlertsTab, setSampleAlertsTab] = useState<
    "networkEvents" | "health" | "connections"
  >("networkEvents");

  return (
    <div className="flex flex-col gap-10">
      {/* Page hero */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Cadenza · Design System
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Tokens, primitives, and patterns. Every component used in the Opus
            React frontend lives here — this page is the source of truth for
            design + engineering.
          </p>
        </div>
      </div>

      {/* Sample-data disclosure. This page is an internal component
          reference and is not linked from nav — but it is reachable by
          URL. Every number / serial / latency below is hardcoded for
          layout purposes; nothing here reflects real fleet state. */}
      <div className="flex items-start gap-3 rounded-xl border border-peach/30 bg-peach/8 px-4 py-3 text-xs text-fg-dim">
        <Info size={14} className="mt-0.5 shrink-0 text-peach" />
        <div>
          <strong className="text-fg">Sample data.</strong> This is a component
          reference page, not a live dashboard. All numbers, serials, latencies,
          and statuses below are hardcoded to demonstrate the visual states —
          they do not reflect any real device or workflow.
        </div>
      </div>

      <DsPrimitivesSection name={name} setName={setName} />
      <DsUiSection />
      <DsDashboardSection
        sampleSite={sampleSite}
        setSampleSite={setSampleSite}
        sampleRange={sampleRange}
        setSampleRange={setSampleRange}
        sampleAlertsTab={sampleAlertsTab}
        setSampleAlertsTab={setSampleAlertsTab}
      />
      <DsAdvancedSection themeMode={themeMode} />
      <DsTableSection />
    </div>
  );
}
